import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { StytchB2B, useStytchMember, useStytchMemberSession, useStytchOrganization } from '@stytch/react/b2b';
import {
    AuthFlowType, B2BOAuthProviders,
    B2BProducts,
    StyleConfig,
    StytchB2BUIConfig,
} from '@stytch/vanilla-js';
import { graphql } from 'gql';
import { usePostHog } from 'posthog-js/react';
import React, { lazy, useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import audioloIcon from './assets/Icon.png';
import Suspenseful from './Components/Suspenseful';
import { SnackbarContext } from './Providers/SnackbarProvider';
import useBrowserCheck from './util/useBrowserCheck';
import config from './config';
import Layout from './Layout';
import LayoutPartial from './Layout/LayoutPartial';
import { EmbeddedSandbox } from './Sandbox';
import useSessionRefresh from './util/useSessionRefresh';
import Index from './Views/index';

const Episode = lazy(
    () => import(/* webpackChunkName: "view.episode" */ './Views/Episode')
);
const RecordingSession = lazy(() => import(/* webpackChunkName: "view.recording-session" */ './Views/RecordingSession'));
const RecordingSessionDraft = lazy(() => import(/* webpackChunkName: "view.recording-session-draft" */ './Views/RecordingSessionDraft'));
const Organization = lazy(() => import(/* webpackChunkName: "view.organization" */ './Views/Organization'));
const ScriptEditor = lazy(() => import(/* webpackChunkName: "view.script-editor" */ './Views/ScriptEditor'));

const BeginningPathsToAllowDragAndDrop = [ '/episode' ];

const Login = () => {
    const { session, fromCache } = useStytchMemberSession();
    const navigate = useNavigate();

    useEffect(() => {
        if (session && !fromCache) {
            const timeout = setTimeout(() => {
                navigate('/');
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [session, navigate, fromCache]);

    const style = {
        logo: {
            logoImageUrl: audioloIcon,
        },
    } as StyleConfig;

    // Example config for the Discovery auth flow, hosted on a generic login page
    const discoveryConfig = {
        authFlowType: AuthFlowType.Discovery,
        products: [B2BProducts.emailMagicLinks, B2BProducts.oauth],
        emailMagicLinksOptions: {
            loginRedirectURL: `${window.location.origin}/login`,
            discoveryRedirectURL: `${window.location.origin}/login`,
            signupRedirectURL: `${window.location.origin}/login`,
        },
        oauthOptions: {
            loginRedirectURL: `${window.location.origin}/login`,
            discoveryRedirectURL: `${window.location.origin}/login`,
            signupRedirectURL: `${window.location.origin}/login`,
            providers: [B2BOAuthProviders.Google],
        },
        sessionOptions: {
            sessionDurationMinutes: parseInt(config.sessionDurationMinutes),
        },
    } as StytchB2BUIConfig;

    return (
        <Box sx={{ mx: 'auto', mt: 20, maxWidth: 'fit-content' }}>
            <StytchB2B config={discoveryConfig} styles={style} />
        </Box>
    );
};

const OrgLogin = () => {
    const { session, fromCache } = useStytchMemberSession();
    const { organization } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (session && !fromCache) {
            const timeout = setTimeout(() => {
                navigate('/');
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [session, navigate, fromCache]);

    const style = {
        logo: {
            logoImageUrl: audioloIcon,
        },
    } as StyleConfig;

    // Example config for the Discovery auth flow, hosted on a generic login page
    const discoveryConfig = {
        authFlowType: AuthFlowType.Organization,
        products: [B2BProducts.passwords],
        emailMagicLinksOptions: {
            loginRedirectURL: `${window.location.origin}/login`,
            discoveryRedirectURL: `${window.location.origin}/login`,
            signupRedirectURL: `${window.location.origin}/login`,
        },
        passwordOptions: {
            loginRedirectURL: `${window.location.origin}/login`,
            resetPasswordRedirectURL: `${window.location.origin}/organization/${organization}/login`,
            resetPasswordExpirationMinutes: 20,
        },
        sessionOptions: {
            sessionDurationMinutes: parseInt(config.sessionDurationMinutes),
        },
    } as StytchB2BUIConfig;

    return (
        <Box sx={{ mx: 'auto', mt: 20, maxWidth: 'fit-content' }}>
            <StytchB2B config={discoveryConfig} styles={style} />
        </Box>
    );
}

const AppVersionQuery = graphql(`
    query AppVersion {
        appVersion {
            version
        }
    }
`);

function RouteWrapper() {
    const { member } = useStytchMember();
    const { organization } = useStytchOrganization();
    const posthog = usePostHog();
    const location = useLocation();

    const { warning } = useContext(SnackbarContext);

    const [posthogLoaded, setPosthogLoaded] = useState(false);

    const { data, loading } = useQuery(AppVersionQuery, { pollInterval: 10000, notifyOnNetworkStatusChange: true });

    const prevLocationKey = useRef<string>();

    useEffect(() => {
        const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile)
            alert('The beta version of this application is currently designed for desktop use. ' +
                'Please use a desktop browser for the best experience.');
    }, []);

    useEffect(() => {
        if (data && data.appVersion && !loading) {
            if (data.appVersion.version !== config.appVersion) {
                warning('This application has been updated. Please refresh your browser to get the latest version.');
            }
        }
    }, [data, loading, warning]);

    // Some users seem to try to drag and drop in the wrong context, where a drag and drop doesn't exist
    // This causes the browser to try to open the file in the tab, which is not what we want
    // So we block that behavior
    useEffect(() => {
        const dragEvent  = (e: DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'none';
                e.dataTransfer.dropEffect = 'none';
            }
        }

        if (!BeginningPathsToAllowDragAndDrop.some(p => location.pathname.toLowerCase().startsWith(p.toLowerCase()))) {
            window.addEventListener('dragenter', dragEvent);
            window.addEventListener('dragover', dragEvent);
            window.addEventListener('drop', dragEvent);
        }

        return () => {
            window.removeEventListener('dragenter', dragEvent);
            window.removeEventListener('dragover', dragEvent);
            window.removeEventListener('drop', dragEvent);
        }
    }, [location]);

    useEffect(() => {
        if (location.key!== prevLocationKey.current) {
            posthog.capture('$pageview')
        }
        prevLocationKey.current = location.key;
    }, [location, posthog]);

    useEffect(() => {
        if (!member || !organization || posthogLoaded) return;

        posthog.identify(member?.member_id, {
            email: member?.email_address,
            organization: organization.organization_id,
            organization_name: organization.organization_name,
        });
        posthog.group('organization', organization.organization_id, {
            name: organization.organization_name,
            slug: organization.organization_slug,
        });

        setPosthogLoaded(true);
    }, [member, organization, posthog, posthogLoaded]);

    return (
        <>
            <Outlet />
        </>
    )
}

const AppRoutes = () => {
    const { session } = useStytchMemberSession();
    useBrowserCheck();
    useSessionRefresh();

    const onLogin = window.location.pathname.toLowerCase().split('/').lastItem === 'login';

    if (!session && !onLogin) {
        window.location.href = '/login';
        return null;
    }

    return (
        <BrowserRouter>
            <Routes>
                <Route Component={RouteWrapper}>
                    <Route path='/login' element={<Login />} />
                    <Route path='/organization/:organization/login' element={<OrgLogin />} />
                    {process.env.NODE_ENV === 'development' && <Route path='sandbox' element={<EmbeddedSandbox />} />}
                    <Route path='/' element={<Layout />}>
                        <Route index element={<Index />} />
                        <Route path='episode/:episodeId' element={Suspenseful(Episode)} />
                        <Route path='recording-session'>
                            <Route path=':sessionId' element={Suspenseful(RecordingSession)} />
                            <Route path='draft' element={Suspenseful(RecordingSessionDraft)} />
                            <Route path='draft/:sessionId' element={Suspenseful(RecordingSessionDraft)} />
                        </Route>
                        <Route path='script-editor/:episodeId' element={Suspenseful(ScriptEditor)} />
                    </Route>
                    <Route path='/' element={<LayoutPartial />}>
                        <Route path='organization' element={Suspenseful(Organization)} />
                    </Route>
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default AppRoutes;